<template>
  <!-- same format with positions.vue -->
  <div>
    <v-btn @click="dialog = true"
      ><v-icon class="mr-2" color="green">mdi-hand-coin</v-icon> Cash
      Advances</v-btn
    >
    <v-dialog v-model="dialog" max-width="1100px" persistent>
      <v-card class="pa-3">
        <v-card-title> <h1>Cash Advances</h1></v-card-title>
        <!-- add cash advance card -->
        <v-card-text>
          <v-row>
            <v-col>
              <v-select
                prepend-inner-icon="mdi-account-circle"
                label="Employee"
                dense
                outlined
                item-text="name"
                item-value="id"
                :items="employees"
                v-model="cash_advance.employee_id"
              ></v-select>
              <v-text-field
                type="number"
                prepend-inner-icon="mdi-cash"
                class="mt-n2"
                label="Amount"
                dense
                outlined
                v-model="cash_advance.amount"
              ></v-text-field>
              <!-- date issued -->
              <div class="d-flex">
                <p class="mr-2">Date Issued</p>
                <input
                  type="date"
                  v-model="cash_advance.date_issued"
                  class="mt-n3"
                />
              </div>
            </v-col>
            <v-col>
              <v-text-field
                prepend-inner-icon="mdi-note-text"
                class="mt-n2"
                label="Reason"
                dense
                outlined
                v-model="cash_advance.reason"
              ></v-text-field>
              <!-- chart of accounts -->

              <v-select
                prepend-inner-icon="mdi-cash"
                label="Payment Method"
                dense
                outlined
                item-text="account_title"
                item-value="id"
                :items="chart_of_accounts"
                v-model="cash_advance.chart_of_account_id"
              ></v-select>
            </v-col>
          </v-row>

          <!-- Cash advance summary for the selected employee -->
          <v-card
            v-if="employeeSummary"
            outlined
            class="mb-4 mt-n2"
            color="blue lighten-5"
          >
            <v-card-subtitle class="font-weight-bold pb-1">
              <v-icon small class="mr-1" color="primary"
                >mdi-account-cash</v-icon
              >
              Cash Advance Summary — {{ employeeSummary.name }}
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <v-row dense>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text text--darken-1">
                    Total Cash Advances
                  </div>
                  <div class="text-h6 font-weight-bold">
                    {{ formatCurrency(employeeSummary.totalAmount) }}
                  </div>
                  <div class="text-caption grey--text">
                    {{ employeeSummary.count }} record(s)
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text text--darken-1">
                    Total Paid
                  </div>
                  <div class="text-h6 font-weight-bold green--text">
                    {{ formatCurrency(employeeSummary.totalPaid) }}
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text text--darken-1">
                    Outstanding Balance
                  </div>
                  <div class="text-h6 font-weight-bold red--text">
                    {{ formatCurrency(employeeSummary.totalBalance) }}
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="text-caption grey--text text--darken-1 mb-1">
                    Status Breakdown
                  </div>
                  <div class="d-flex flex-wrap" style="gap: 4px">
                    <v-chip
                      x-small
                      color="green"
                      dark
                      v-if="employeeSummary.paidCount"
                      >Paid: {{ employeeSummary.paidCount }}</v-chip
                    >
                    <v-chip
                      x-small
                      color="red"
                      dark
                      v-if="employeeSummary.unpaidCount"
                      >Unpaid: {{ employeeSummary.unpaidCount }}</v-chip
                    >
                    <v-chip
                      x-small
                      color="amber darken-2"
                      dark
                      v-if="employeeSummary.deductedCount"
                      >Deducted: {{ employeeSummary.deductedCount }}</v-chip
                    >
                    <v-chip
                      x-small
                      color="grey"
                      dark
                      v-if="employeeSummary.pendingCount"
                      >Pending: {{ employeeSummary.pendingCount }}</v-chip
                    >
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-btn color="primary" @click="submit" :loading="loading"
            >Submit</v-btn
          >

          <v-btn color="success" @click="payCADialog = true" class="ml-2"
            ><v-icon class="mr-2">mdi-cash-multiple</v-icon>Pay CA
            Manually</v-btn
          >

          <v-btn color="info" @click="openHistoryDialog" class="ml-2"
            ><v-icon class="mr-2">mdi-history</v-icon>Payment History</v-btn
          >

          <v-btn
            color="deep-purple"
            dark
            @click="openOutstandingDialog"
            class="ml-2"
            ><v-icon class="mr-2">mdi-account-cash</v-icon>Outstanding
            Balances</v-btn
          >

          <div class="d-flex">
            <v-text-field
              label="Search"
              v-model="search"
              prepend-icon="mdi-magnify "
              class="mr-5"
            ></v-text-field>
            <v-select
              v-model="selectedBranchId"
              :items="branchOptions"
              item-text="branch_name"
              item-value="id"
              label="Branch"
              clearable
              style="max-width: 300px"
              @change="onBranchChange"
            ></v-select>
          </div>
          <!-- cash advances data table here -->
          <v-data-table
            :items="cash_advances"
            :headers="headers"
            dense
            :search="search"
          >
            <template v-slot:item.actions="{ item }">
              <!-- edit -->

              <div class="d-flex">
                <v-btn icon color="warning" @click="editCashAdvance(item)"
                  ><v-icon>mdi-pencil</v-icon></v-btn
                >
                <v-btn
                  v-if="item.status !== 'cancelled'"
                  icon
                  color="primary"
                  title="Undo cash advance"
                  @click="undoCashAdvance(item)"
                  ><v-icon>mdi-undo-variant</v-icon></v-btn
                >
                <v-btn icon color="red" @click="del(item)"
                  ><v-icon>mdi-delete</v-icon></v-btn
                >
              </div>
            </template>
            <!-- status with colors 
             1. if unpaid = red
             2. if paid = green 
             3. if deducted = yellow
            -->

            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item.status)" dark>{{
                item.status
              }}</v-chip>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="dialog = false">Close</v-btn>
        </v-card-actions></v-card
      ></v-dialog
    >
    <!-- lazy components -->
    <edit-cash-advance-modal
      v-if="editDialog"
      :item="editItem"
      @close="editDialog = false"
    ></edit-cash-advance-modal>

    <!-- Pay CA Manually Dialog -->
    <v-dialog v-model="payCADialog" max-width="900px" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon class="mr-2" color="white">mdi-cash-multiple</v-icon>
          Pay Cash Advance Manually
        </v-card-title>

        <v-card-text class="mt-4">
          <!-- Employee Search -->
          <v-autocomplete
            v-model="payCA.employee_id"
            :items="employees"
            item-text="name"
            item-value="id"
            label="Search Employee"
            prepend-inner-icon="mdi-account-search"
            outlined
            dense
            clearable
            @change="loadEmployeePendingCAs"
          ></v-autocomplete>

          <!-- Display Pending CAs -->
          <v-card v-if="pendingCAs.length > 0" outlined class="mb-4">
            <v-card-subtitle class="font-weight-bold">
              Pending Cash Advances (Total:
              {{ formatCurrency(totalPendingAmount) }})
            </v-card-subtitle>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Date Issued</th>
                    <th>Amount</th>
                    <th>Total Paid</th>
                    <th>Balance</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ca in pendingCAs" :key="ca.id">
                    <td>{{ ca.date_issued }}</td>
                    <td>{{ formatCurrency(ca.amount) }}</td>
                    <td>{{ formatCurrency(ca.total_paid) }}</td>
                    <td class="font-weight-bold text-red">
                      {{ formatCurrency(ca.balance) }}
                    </td>
                    <td>{{ ca.reason }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Payment Details -->
          <v-row v-if="pendingCAs.length > 0">
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="payCA.payment_amount"
                type="number"
                label="Payment Amount"
                prepend-inner-icon="mdi-cash"
                outlined
                dense
                :rules="[rules.required, rules.maxAmount]"
                @input="calculateDistribution"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="payCA.payment_method_id"
                :items="chart_of_accounts"
                item-text="account_title"
                item-value="id"
                label="Payment Method"
                prepend-inner-icon="mdi-wallet"
                outlined
                dense
                :rules="[rules.required]"
              ></v-select>
            </v-col>
          </v-row>

          <!-- Payment Distribution Preview -->
          <v-card
            v-if="paymentDistribution.length > 0"
            outlined
            color="blue lighten-5"
            class="mb-4"
          >
            <v-card-subtitle class="font-weight-bold">
              Payment Distribution (Remaining:
              {{ formatCurrency(remainingBalance) }})
            </v-card-subtitle>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>CA ID</th>
                    <th>Original Balance</th>
                    <th>Payment Applied</th>
                    <th>New Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="dist in paymentDistribution" :key="dist.ca_id">
                    <td>{{ dist.ca_id }}</td>
                    <td>{{ formatCurrency(dist.original_balance) }}</td>
                    <td class="font-weight-bold text-green">
                      {{ formatCurrency(dist.payment_applied) }}
                    </td>
                    <td
                      :class="
                        dist.new_balance === 0
                          ? 'text-green font-weight-bold'
                          : 'text-orange font-weight-bold'
                      "
                    >
                      {{ formatCurrency(dist.new_balance) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Journal Entry Preview -->
          <v-card
            v-if="paymentDistribution.length > 0"
            outlined
            color="purple lighten-5"
          >
            <v-card-subtitle class="font-weight-bold">
              Journal Entry Preview
            </v-card-subtitle>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th class="text-right">Debit</th>
                    <th class="text-right">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ getPaymentMethodName() }}</td>
                    <td class="text-right font-weight-bold">
                      {{ formatCurrency(payCA.payment_amount) }}
                    </td>
                    <td class="text-right">-</td>
                  </tr>
                  <tr>
                    <td>Advances to Employees</td>
                    <td class="text-right">-</td>
                    <td class="text-right font-weight-bold">
                      {{ formatCurrency(payCA.payment_amount) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closePayCADialog">Cancel</v-btn>
          <v-btn
            color="success"
            :disabled="!isPaymentValid"
            :loading="payLoading"
            @click="processPayment"
          >
            <v-icon class="mr-2">mdi-check</v-icon>
            Process Payment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Pay CA Manually History Dialog -->
    <v-dialog v-model="historyDialog" max-width="1000px">
      <v-card>
        <v-card-title class="headline info white--text">
          <v-icon class="mr-2" color="white">mdi-history</v-icon>
          Pay CA Manually — Payment History
        </v-card-title>
        <v-card-text class="mt-4">
          <v-text-field
            label="Search"
            v-model="historySearch"
            prepend-icon="mdi-magnify"
          ></v-text-field>
          <v-data-table
            :items="payHistory"
            :headers="historyHeaders"
            :loading="historyLoading"
            :search="historySearch"
            dense
          >
            <template v-slot:item.created_at="{ item }">
              {{ formatDateTime(item.created_at) }}
            </template>
            <template v-slot:item.payment_amount="{ item }">
              {{ formatCurrency(item.payment_amount) }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="historyDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Outstanding CA Balances Dialog (JMB-129) -->
    <v-dialog v-model="outstandingDialog" max-width="900px">
      <v-card>
        <v-card-title class="headline deep-purple white--text">
          <v-icon class="mr-2" color="white">mdi-account-cash</v-icon>
          Outstanding Cash Advance Balances
        </v-card-title>
        <v-card-text class="mt-4">
          <v-alert dense text type="info" class="mb-2">
            Total outstanding:
            <strong>{{ formatCurrency(totalOutstanding) }}</strong> across
            {{ outstandingBalances.length }} employee(s)
          </v-alert>
          <v-text-field
            label="Search"
            v-model="outstandingSearch"
            prepend-icon="mdi-magnify"
          ></v-text-field>
          <v-data-table
            :items="outstandingBalances"
            :headers="outstandingHeaders"
            :loading="outstandingLoading"
            :search="outstandingSearch"
            dense
          >
            <template v-slot:item.total_released="{ item }">
              {{ formatCurrency(item.total_released) }}
            </template>
            <template v-slot:item.total_recovered="{ item }">
              {{ formatCurrency(item.total_recovered) }}
            </template>
            <template v-slot:item.outstanding="{ item }">
              <span class="font-weight-bold red--text">{{
                formatCurrency(item.outstanding)
              }}</span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="outstandingDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  data() {
    return {
      editDialog: false,
      editItem: {},
      dialog: false,
      search: "",
      selectedBranchId: null,
      loading: false,
      cash_advance: {
        employee_id: null,
        amount: null,
        reason: "",
        date_issued: null,
        chart_of_account_id: 70,
      },
      headers: [
        { text: "Actions", value: "actions", sortable: false },
        { text: "Employee", value: "employee.name" },
        { text: "Amount", value: "amount" },
        { text: "Total Paid", value: "total_paid" },
        { text: " Balance", value: "balance" },
        { text: "Payment Method", value: "chart_of_account.account_title" },
        { text: "Reason", value: "reason" },
        { text: "Date Issued", value: "date_issued" },
        { text: "Transacted By", value: "inserted_by" },
        { text: "Status", value: "status" },
      ],
      // Pay CA Manually
      payCADialog: false,
      payLoading: false,
      pendingCAs: [],
      paymentDistribution: [],
      payCA: {
        employee_id: null,
        payment_amount: 0,
        payment_method_id: null,
      },
      rules: {
        required: (v) => !!v || "This field is required",
        maxAmount: (v) =>
          v <= this.totalPendingAmount ||
          `Amount cannot exceed ${this.formatCurrency(
            this.totalPendingAmount
          )}`,
      },
      // Pay CA Manually history
      historyDialog: false,
      historyLoading: false,
      historySearch: "",
      payHistory: [],
      historyHeaders: [
        { text: "Date", value: "created_at" },
        { text: "Employee", value: "employee.name" },
        { text: "Amount", value: "payment_amount" },
        { text: "Payment Method", value: "chart_of_account.account_title" },
        { text: "Inserted By", value: "inserted_by" },
      ],
      // Outstanding balances (JMB-129)
      outstandingDialog: false,
      outstandingLoading: false,
      outstandingSearch: "",
      outstandingBalances: [],
      outstandingHeaders: [
        { text: "Employee", value: "employee.name" },
        { text: "Open CAs", value: "open_count" },
        { text: "Total Released", value: "total_released" },
        { text: "Total Recovered", value: "total_recovered" },
        { text: "Outstanding", value: "outstanding" },
      ],
    };
  },
  components: {
    EditCashAdvanceModal: () => import("./editCashAdvanceModal.vue"),
  },
  computed: {
    ...mapGetters({
      employees: "employee/employees",
      cash_advances: "cash_advance/cash_advances",
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
      chart_of_accounts: "chart_of_account/chart_of_accounts",
      branches: "branch/branches",
    }),
    branchOptions() {
      return this.branches || [];
    },
    // Aggregated cash advance figures for the employee selected in the
    // top form. Returns null when no employee is selected.
    employeeSummary() {
      const id = this.cash_advance.employee_id;
      if (!id) return null;

      // Cancelled (undone) advances are excluded: their journals were
      // reversed, so they must not count toward released/paid totals.
      const list = this.cash_advances.filter(
        (ca) =>
          (ca.employee_id || (ca.employee && ca.employee.id)) == id &&
          ca.status !== "cancelled"
      );

      const num = (v) => parseFloat(v || 0);
      const employee = this.employees.find((e) => e.id == id);
      const name =
        (employee && employee.name) ||
        (list[0] && list[0].employee && list[0].employee.name) ||
        "Employee";

      return {
        name,
        count: list.length,
        totalAmount: list.reduce((s, ca) => s + num(ca.amount), 0),
        totalPaid: list.reduce((s, ca) => s + num(ca.total_paid), 0),
        totalBalance: list.reduce((s, ca) => s + num(ca.balance), 0),
        paidCount: list.filter((ca) => ca.status === "paid").length,
        unpaidCount: list.filter((ca) => ca.status === "unpaid").length,
        deductedCount: list.filter((ca) => ca.status === "deducted").length,
        // Records with no status yet (shown as grey dots in the table).
        pendingCount: list.filter(
          (ca) => !["paid", "unpaid", "deducted"].includes(ca.status)
        ).length,
      };
    },
    totalPendingAmount() {
      return this.pendingCAs.reduce(
        (total, ca) => total + parseFloat(ca.balance || 0),
        0
      );
    },
    remainingBalance() {
      const totalApplied = this.paymentDistribution.reduce(
        (sum, dist) => sum + dist.payment_applied,
        0
      );
      return this.totalPendingAmount - totalApplied;
    },
    totalOutstanding() {
      return this.outstandingBalances.reduce(
        (sum, row) => sum + parseFloat(row.outstanding || 0),
        0
      );
    },
    isPaymentValid() {
      return (
        this.payCA.employee_id &&
        this.payCA.payment_amount > 0 &&
        this.payCA.payment_amount <= this.totalPendingAmount &&
        this.payCA.payment_method_id &&
        this.paymentDistribution.length > 0
      );
    },
  },
  methods: {
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    ...mapActions({
      add_cash_advance: "cash_advance/add_cash_advance",
      cancel_cash_advance: "cash_advance/cancel_cash_advance",
      delete_cash_advance: "cash_advance/delete_cash_advance",
      get_cash_advances: "cash_advance/get_cash_advances",
      get_chart_of_accounts: "chart_of_account/get_chart_of_accounts",
    }),
    onBranchChange() {
      // Refetch cash advances and the employee dropdowns for the branch
      this.get_cash_advances(this.selectedBranchId);
      this.$store.dispatch("employee/get_employees", this.selectedBranchId);
    },
    getStatusColor(status) {
      if (status === "unpaid") return "red";
      else if (status === "paid") return "green";
      else if (status === "deducted") return "yellow";
      else return "grey"; // cancelled and anything else
    },
    async editCashAdvance(item) {
      // gated by the employees.edit_cash_advance permission (hat bundle)
      if (!this.can("employees.edit_cash_advance")) {
        this.$alert({
          title: "Error",
          message: "You are not authorized to edit cash advances.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }

      this.editItem = item;
      this.editDialog = true;
    },
    async submit() {
      this.loading = true;
      let x = await this.$confirm({
        title: "Add Cash Advance",
        message: "Are you sure you want to add this cash advance?",
        color: "primary",
        icon: "mdi-plus",
      });
      if (x) {
        try {
          await this.add_cash_advance(this.cash_advance);
          this.cash_advance = {
            employee_id: null,
            amount: null,
            reason: "",
            date_issued: null,
          };
          this.$alert({
            title: "Success",
            message: "Successfully added a cash advance",
            color: "success",
            icon: "mdi-check-circle",
          });
        } catch (error) {
          console.error("Error adding cash advance:", error);
        } finally {
          this.loading = false;
        }
      }
    },
    async undoCashAdvance(item) {
      // gated by the same permission as editing a cash advance
      if (!this.can("employees.edit_cash_advance")) {
        this.$alert({
          title: "Error",
          message: "You are not authorized to undo cash advances.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      let x = await this.$confirm({
        title: "Undo Cash Advance",
        message:
          "Undo this cash advance? All of its journal entries will be reversed and it will be marked as cancelled.",
        color: "primary",
        icon: "mdi-undo-variant",
      });
      if (x) {
        this.loading = true;
        try {
          await this.cancel_cash_advance(item.id);
          await this.get_cash_advances(this.selectedBranchId);
          this.$alert({
            title: "Success",
            message: "Cash advance undone and journal entries reversed.",
            color: "success",
            icon: "mdi-check-circle",
          });
        } catch (error) {
          this.$alert({
            title: "Error",
            message:
              error.response?.data?.message ||
              "Failed to undo the cash advance.",
            color: "error",
            icon: "mdi-alert-circle",
          });
        } finally {
          this.loading = false;
        }
      }
    },
    async del(item) {
      let x = await this.$confirm({
        title: "Delete Cash Advance",
        message: "Are you sure you want to delete this cash advance?",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        this.loading = true;
        try {
          await this.delete_cash_advance(item);
          this.$alert({
            title: "Success",
            message: "Successfully deleted a cash advance",
            color: "success",
            icon: "mdi-check-circle",
          });
        } catch (error) {
          this.$alert({
            title: "Error",
            message:
              error.response?.data?.message ||
              "Failed to delete the cash advance.",
            color: "error",
            icon: "mdi-alert-circle",
          });
        } finally {
          this.loading = false;
        }
      }
    },
    // Pay CA Manually Methods
    async loadEmployeePendingCAs() {
      if (!this.payCA.employee_id) {
        this.pendingCAs = [];
        this.paymentDistribution = [];
        return;
      }

      try {
        // Get pending CAs for the selected employee
        const response = await this.$store.dispatch(
          "cash_advance/get_employee_pending_cas",
          this.payCA.employee_id
        );
        this.pendingCAs = response.filter(
          (ca) =>
            (ca.status === "unpaid" || ca.status === "deducted") &&
            parseFloat(ca.balance) > 0
        );

        // Sort by date issued (oldest first)
        this.pendingCAs.sort(
          (a, b) => new Date(a.date_issued) - new Date(b.date_issued)
        );

        // Reset payment amount and distribution
        this.payCA.payment_amount = 0;
        this.paymentDistribution = [];
      } catch (error) {
        console.error("Error loading pending CAs:", error);
        this.$alert({
          title: "Error",
          message: "Failed to load pending cash advances",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    calculateDistribution() {
      if (!this.payCA.payment_amount || this.payCA.payment_amount <= 0) {
        this.paymentDistribution = [];
        return;
      }

      let remainingPayment = parseFloat(this.payCA.payment_amount);
      const distribution = [];

      // Allocate payment to CAs (oldest first)
      for (const ca of this.pendingCAs) {
        if (remainingPayment <= 0) break;

        const caBalance = parseFloat(ca.balance);
        const paymentApplied = Math.min(remainingPayment, caBalance);
        const newBalance = caBalance - paymentApplied;

        distribution.push({
          ca_id: ca.id,
          original_balance: caBalance,
          payment_applied: paymentApplied,
          new_balance: newBalance,
        });

        remainingPayment -= paymentApplied;
      }

      this.paymentDistribution = distribution;
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
      }).format(amount || 0);
    },
    formatDateTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("en-PH");
    },
    async openHistoryDialog() {
      this.historyDialog = true;
      this.historyLoading = true;
      try {
        this.payHistory = await this.$store.dispatch(
          "cash_advance/get_pay_ca_history"
        );
      } catch (error) {
        console.error("Error loading payment history:", error);
        this.$alert({
          title: "Error",
          message: "Failed to load payment history",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.historyLoading = false;
      }
    },
    async openOutstandingDialog() {
      this.outstandingDialog = true;
      this.outstandingLoading = true;
      try {
        this.outstandingBalances = await this.$store.dispatch(
          "cash_advance/get_outstanding_balances"
        );
      } catch (error) {
        console.error("Error loading outstanding balances:", error);
        this.$alert({
          title: "Error",
          message: "Failed to load outstanding CA balances",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.outstandingLoading = false;
      }
    },
    getPaymentMethodName() {
      const method = this.chart_of_accounts.find(
        (acc) => acc.id === this.payCA.payment_method_id
      );
      return method ? method.account_title : "N/A";
    },
    closePayCADialog() {
      this.payCADialog = false;
      this.payCA = {
        employee_id: null,
        payment_amount: 0,
        payment_method_id: null,
      };
      this.pendingCAs = [];
      this.paymentDistribution = [];
    },
    async processPayment() {
      if (!this.isPaymentValid) {
        this.$alert({
          title: "Validation",
          message: "Please fill in all required fields correctly.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      const confirmed = await this.$confirm({
        title: "Process Payment",
        message: `Process payment of ${this.formatCurrency(
          this.payCA.payment_amount
        )} for ${this.paymentDistribution.length} cash advance(s)?`,
        color: "primary",
        icon: "mdi-help-circle-outline",
      });

      if (!confirmed) return;

      this.payLoading = true;

      try {
        // Prepare payload
        const payload = {
          employee_id: this.payCA.employee_id,
          payment_amount: this.payCA.payment_amount,
          payment_method_id: this.payCA.payment_method_id,
          payment_distribution: this.paymentDistribution,
        };

        // Call backend API to process payment
        await this.$store.dispatch(
          "cash_advance/pay_cash_advance_manually",
          payload
        );

        this.$alert({
          title: "Success",
          message: "Payment processed successfully!",
          color: "success",
          icon: "mdi-check-circle",
        });

        // Refresh cash advances
        await this.get_cash_advances();

        // Close dialog
        this.closePayCADialog();
      } catch (error) {
        console.error("Error processing payment:", error);
        this.$alert({
          title: "Error",
          message:
            "Failed to process payment: " +
            (error.response?.data?.message || error.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.payLoading = false;
      }
    },
  },
  mounted() {
    if (this.cash_advances.length == 0) this.get_cash_advances();
    if (this.chart_of_accounts.length == 0) this.get_chart_of_accounts();
    if (!this.branches || this.branches.length === 0) {
      this.$store.dispatch("branch/get_branches");
    }
    this.selectedBranchId = this.user.branch_id;
  },
};
</script>

<style></style>
